import { useQuery } from "@tanstack/react-query";
import axiosInstance from "@/lib/axios";
import {
  gmailListAccountsEndpoint,
  gmailMyAccountEndpoint,
} from "@/config/endpoints";
import type { GmailAccount } from "../types/gmail.types";

/**
 * The workspace Gmail this user sends from, or null if none — one Gmail per
 * workspace, connected by an admin and used by everyone.
 *
 * Endpoint shape (from src/core/gmail/gmail.account.controller.ts):
 *   GET /api/v1/integrations/gmail/accounts/me
 *   200 { data: GmailAccount | null,
 *         meta: { canManage, canReadInbox, activeAccounts } }
 */
export const GMAIL_ACCOUNT_QUERY_KEY = ["gmail", "account", "me"];
export const GMAIL_ACCOUNT_LIST_QUERY_KEY = ["gmail", "account", "list"];

function adapt(raw: any): GmailAccount | null {
  if (!raw || !raw.id) return null;
  return {
    id: raw.id,
    userId: raw.userId,
    email: raw.email,
    displayName: raw.displayName ?? null,
    status: raw.status,
    scopes: raw.scopes ?? [],
    tokenExpiresAt: raw.tokenExpiresAt ?? null,
    lastErrorMessage: raw.lastErrorMessage ?? null,
    createdAt: raw.createdAt,
    updatedAt: raw.updatedAt,
    connectedBy: raw.connectedBy ?? null,
    isPrimary: raw.isPrimary ?? true,
    inboxAudience: raw.inboxAudience === "everyone" ? "everyone" : "admins",
  };
}

export function useGmailAccount() {
  const query = useQuery({
    queryKey: GMAIL_ACCOUNT_QUERY_KEY,
    queryFn: async () => {
      const res = await axiosInstance.get(gmailMyAccountEndpoint);
      const meta = res.data?.meta ?? {};
      return {
        account: adapt(res.data?.data ?? null),
        canManage: Boolean(meta.canManage),
        canReadInbox: Boolean(meta.canReadInbox),
        activeAccounts: Number(meta.activeAccounts ?? 0),
      };
    },
    staleTime: 1000 * 60 * 5,
    retry: 1,
  });

  return {
    account: query.data?.account ?? null,
    /** Connect / reconnect / disconnect, inbox settings (admins). */
    canManage: query.data?.canManage ?? false,
    canReadInbox: query.data?.canReadInbox ?? false,
    /** More than 1 only for workspaces from the per-user era. */
    activeAccounts: query.data?.activeAccounts ?? 0,
    isLoading: query.isLoading,
    error: query.error,
    refetch: query.refetch,
  };
}

/** Every active account (managers) — to remove per-user-era extras. */
export function useGmailAccountList(enabled: boolean) {
  const query = useQuery({
    queryKey: GMAIL_ACCOUNT_LIST_QUERY_KEY,
    queryFn: async () => {
      const res = await axiosInstance.get(gmailListAccountsEndpoint);
      const list: unknown[] = Array.isArray(res.data?.data) ? res.data.data : [];
      return list.map(adapt).filter((a): a is GmailAccount => a !== null);
    },
    enabled,
    staleTime: 1000 * 60,
  });
  return { accounts: query.data ?? [], isLoading: query.isLoading };
}
